// New Intelligenic Chatbot Script Engine
type ChatbotStep = 'TYPE' | 'NAME' | 'FILES' | 'FILE_ANALYSIS' | 'USER' | 'PROBLEMS' | 'SOLUTION' | 'REVIEW' | 'COMPLETE';

interface ProjectData {
  projectType?: string;
  projectName?: string;
  files?: any[];
  fileAnalysis?: {
    analytics?: string;
    statistics?: string;
    businessCase?: string;
  };
  useFileAnalysis?: boolean;
  targetUser?: string;
  painPoints?: string;
  solution?: string;
  uploadedFiles?: any[];
}

interface ConversationState {
  currentStep: ChatbotStep;
  projectData: ProjectData;
  conversationHistory: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
  stepHistory: string[];
}

type StepDefinition = {
  id: string;
  prompt: string;
  chips: string[];
};

type DefinedChatbotStep = Exclude<ChatbotStep, 'COMPLETE'>;

const CHATBOT_STEPS: Record<DefinedChatbotStep, StepDefinition> = {
  'TYPE': {
    id: 'TYPE',
    prompt: `Welcome to Intelligenic! We'll have you up and running in no time. To get started, tell us about your product:

Are you adding new features to an existing product? Or building a new product from scratch?`,
    chips: ['Existing Product', 'New Product']
  },
  'NAME': {
    id: 'NAME',
    prompt: `Now that we know your product type, let's call it something. Please select a concise, descriptive title (3-5 words). OR, select one of our examples below`,
    chips: ['Sales Enablement Platform', 'Q4 Analytics Dashboard', 'Personalized Wellness App', 'Equipment Maintenance Tracker']
  },
  'FILES': {
    id: 'FILES',
    prompt: `Help us get to know {{projectName}} better.

Upload any relevant files now or close the file uploader for now if you're not ready. No worries, you'll have another chance to share them later!`,
    chips: []
  },
  'FILE_ANALYSIS': {
    id: 'FILE_ANALYSIS',
    prompt: `From your files, {{filesList}}
we identified:
• Analytics: {{analytics}}
• Statistics: {{statistics}}
• Business case: {{businessCase}}

Use these for {{projectName}}?`,
    chips: ['Yes, proceed', 'No, ignore analysis']
  },
  'USER': {
    id: 'USER',
    prompt: `Who are the people, or roles you are designing for? OR, select one of our examples below`,
    chips: [
      'Jordan, Sales Manager at a mid-sized SaaS company',
      'Maya, a 28-year-old fitness enthusiast and busy professional',
      'Sgt. Ramirez, Logistics Officer in the Army'
    ]
  },
  'PROBLEMS': {
    id: 'PROBLEMS',
    prompt: `What are their top frustrations? Provide multiples if you need.

OR, select one of our examples below`,
    chips: [
      'Struggles to access up-to-date sales collateral',
      'Finds it hard to stay motivated without personalized guidance',
      'Manual tracking of maintenance schedules leads to errors'
    ]
  },
  'SOLUTION': {
    id: 'SOLUTION',
    prompt: `What does your solution for {{projectName}} do, who does it help, and why is it valuable? Focus: Who + Problem + Benefit.

OR, select one of our examples below`,
    chips: [
      'A centralized sales enablement platform that helps B2B sales teams access content, track performance, and close deals faster',
      'A personalized wellness app that helps users track fitness, nutrition, and mental health with AI-driven recommendations',
      'A digital maintenance tracker that helps military units monitor equipment health, schedule repairs, and ensure mission readiness'
    ]
  },
  'REVIEW': {
    id: 'REVIEW',
    prompt: `Before we finalize "{{projectName}}", here's a quick summary of your product details:

🏷️ Product Name: {{projectName}}
👥 Primary Users: {{targetUser}}
💡 Solution Overview: {{solution}}
⚠️ Pain Points Addressed: {{painPoints}}

Does everything look accurate? Let us know if you'd like to make any changes.`,
    chips: ['Edit Details', 'Create Product']
  }
};

const STEP_ORDER = ['TYPE', 'NAME', 'FILES', 'FILE_ANALYSIS', 'USER', 'PROBLEMS', 'SOLUTION', 'REVIEW'];

const MOCK_PROJECT_DATA = {
  projectType: 'New Product',
  projectName: 'Sales Enablement Platform',
  targetUser: 'Jordan, Sales Manager at a mid-sized SaaS company',
  painPoints: 'Struggles to access up-to-date sales collateral',
  solution: 'A centralized sales enablement platform that helps B2B sales teams access content, track performance, and close deals faster',
  files: []
};

export class ChatbotScriptEngine {
  private state: ConversationState;

  constructor() {
    this.state = {
      currentStep: 'TYPE',
      projectData: {},
      conversationHistory: [],
      stepHistory: []
    };
  }

  // Initialize conversation
  public startConversation(): string {
    const prompt = this.getCurrentStepPrompt();
    this.addToHistory('assistant', prompt);
    return prompt;
  }

  // Process user input and return next response
  public processUserInput(input: string): string {
    this.addToHistory('user', input);

    // Check for bypass keywords (demo data)
    if (this.checkBypassTrigger(input)) {
      return this.handleBypass();
    }

    // Process the input based on current step
    const response = this.processStepInput(input);
    this.addToHistory('assistant', response);
    
    return response;
  }

  // Handle chip selection - auto-advance to next step
  public selectChip(chipValue: string): string {
    return this.processUserInput(chipValue);
  }

  // Navigate to specific step (for clickable step indicators)
  public navigateToStep(stepId: string): string {
    if (STEP_ORDER.includes(stepId)) {
      this.state.currentStep = stepId as ChatbotStep;
      this.state.stepHistory.push(stepId);
      const prompt = this.getCurrentStepPrompt();
      this.addToHistory('assistant', `Returning to ${stepId} step: ${prompt}`);
      return prompt;
    }
    return 'Invalid step.';
  }

  private processStepInput(input: string): string {
    switch (this.state.currentStep) {
      case 'TYPE':
        return this.handleProjectType(input);
      
      case 'NAME':
        return this.handleProjectName(input);
      
      case 'FILES':
        return this.handleFileUpload(input);
        
      case 'FILE_ANALYSIS':
        return this.handleFileAnalysis(input);
      
      case 'USER':
        return this.handleTargetUser(input);
      
      case 'PROBLEMS':
        return this.handlePainPoints(input);
      
      case 'SOLUTION':
        return this.handleSolutionStatement(input);
      
      case 'REVIEW':
        return this.handleReview(input);
      
      default:
        return 'Something went wrong. Let me restart the conversation.';
    }
  }

  private handleProjectType(input: string): string {
    const normalizedInput = input.toLowerCase().trim();
    
    let projectType = '';
    if (normalizedInput.includes('existing') || normalizedInput === 'existing product') {
      projectType = 'Existing Product';
    } else if (normalizedInput.includes('new') || normalizedInput === 'new product') {
      projectType = 'New Product';
    } else {
      return 'Please choose one of: [Existing Product] [New Product]';
    }

    this.state.projectData.projectType = projectType;
    this.state.currentStep = 'NAME';
    
    return CHATBOT_STEPS.NAME.prompt;
  }

  private handleProjectName(input: string): string {
    const projectName = input.trim();
    this.state.projectData.projectName = projectName;
    this.state.currentStep = 'FILES';
    
    return this.replaceVariables(CHATBOT_STEPS.FILES.prompt);
  }

  private handleFileUpload(input: string): string {
    const normalizedInput = input.toLowerCase().trim();
    
    if (normalizedInput === 'skip' || normalizedInput.includes('skip') || normalizedInput.includes('close')) {
      this.state.projectData.files = [];
      this.state.currentStep = 'USER';
      return CHATBOT_STEPS.USER.prompt;
    } else {
      // Handle uploaded files
      const files = this.extractFileNames(input);
      this.state.projectData.files = files;
      
      if (files.length > 0) {
        // Generate mock file analysis
        this.state.projectData.fileAnalysis = {
          analytics: this.generateMockAnalytics(),
          statistics: this.generateMockStatistics(),
          businessCase: this.generateMockBusinessCase()
        };
        this.state.currentStep = 'FILE_ANALYSIS';
        return this.replaceVariables(CHATBOT_STEPS.FILE_ANALYSIS.prompt);
      }
    }

    // If no files mentioned, go to target user
    this.state.currentStep = 'USER';
    return CHATBOT_STEPS.USER.prompt;
  }

  private handleFileAnalysis(input: string): string {
    const normalizedInput = input.toLowerCase().trim();
    
    if (normalizedInput.includes('yes') || normalizedInput.includes('proceed')) {
      // Accept file analysis - store in project data
      this.state.projectData.useFileAnalysis = true;
    } else {
      // Ignore file analysis
      this.state.projectData.useFileAnalysis = false;
    }

    this.state.currentStep = 'USER';
    return CHATBOT_STEPS.USER.prompt;
  }

  private handleTargetUser(input: string): string {
    this.state.projectData.targetUser = input.trim();
    this.state.currentStep = 'PROBLEMS';
    
    return CHATBOT_STEPS.PROBLEMS.prompt;
  }

  private handlePainPoints(input: string): string {
    this.state.projectData.painPoints = input.trim();
    this.state.currentStep = 'SOLUTION';
    
    return this.replaceVariables(CHATBOT_STEPS.SOLUTION.prompt);
  }

  private handleSolutionStatement(input: string): string {
    this.state.projectData.solution = input.trim();
    this.state.currentStep = 'REVIEW';
    
    return this.replaceVariables(CHATBOT_STEPS.REVIEW.prompt);
  }

  private handleReview(input: string): string {
    const normalizedInput = input.toLowerCase().trim();
    
    if (normalizedInput.includes('create product') || normalizedInput.includes('yes') || normalizedInput.includes('create')) {
      this.state.currentStep = 'COMPLETE';
      return this.generateProjectSummary();
    } else if (normalizedInput.includes('edit details') || normalizedInput.includes('update') || normalizedInput.includes('details')) {
      return `Which section would you like to edit? You can click on any step above to go back and make changes, or:

1. Type "name: [new name]" to change product name
2. Type "user: [new user]" to change target user
3. Type "problems: [new problems]" to change pain points
4. Type "solution: [new solution]" to change solution
5. Type "restart" to start over`;
    }

    return 'Please choose: [Edit Details] or [Create Product]';
  }

  // Helper methods for file handling and mock data generation
  private extractFileNames(input: string): string[] {
    // Extract file names from input (simplified - in real implementation would handle actual uploads)
    const filePattern = /\b\w+\.(pdf|docx|csv|txt|doc|xlsx|pptx)\b/gi;
    return input.match(filePattern) || [];
  }

  private generateMockAnalytics(): string {
    const analytics = [
      'User engagement increased 35% after streamlining workflows',
      'Process efficiency improved by 42% with automation',
      'Customer satisfaction scores rose 28% with better UX',
      'Time-to-completion reduced by 50% through optimization'
    ];
    return analytics[Math.floor(Math.random() * analytics.length)];
  }

  private generateMockStatistics(): string {
    const stats = [
      '89% of users report improved productivity',
      '67% reduction in manual errors detected',
      '3.2x faster processing times measured',
      '92% user adoption rate within first month'
    ];
    return stats[Math.floor(Math.random() * stats.length)];
  }

  private generateMockBusinessCase(): string {
    const cases = [
      'ROI of 240% projected within 18 months',
      'Cost savings of $125K annually through automation',
      'Revenue increase potential of 18% identified',
      'Operational efficiency gains worth $89K per year'
    ];
    return cases[Math.floor(Math.random() * cases.length)];
  }

  // Get current step chips for UI
  public getCurrentStepChips(): string[] {
    return CHATBOT_STEPS[this.state.currentStep as DefinedChatbotStep]?.chips || [];
  }

  // Get step order for progress indicator
  public getStepOrder(): string[] {
    return STEP_ORDER;
  }

  // Get current step index for progress
  public getCurrentStepIndex(): number {
    return STEP_ORDER.indexOf(this.state.currentStep);
  }

  // Helper method to replace variables in prompts
  private replaceVariables(prompt: string): string {
    let result = prompt;
    const data = this.state.projectData;
    
    result = result.replace(/{{projectName}}/g, data.projectName || '[Product Name]');
    result = result.replace(/{{solution}}/g, data.solution || '[Solution Statement]');
    result = result.replace(/{{painPoints}}/g, data.painPoints || '[Pain Points]');
    result = result.replace(/{{projectType}}/g, data.projectType || '[Product Type]');
    result = result.replace(/{{targetUser}}/g, data.targetUser || '[Target User]');
    result = result.replace(/{{filesList}}/g, data.files && data.files.length > 0 ? data.files.join(', ') : 'None');
    
    // File analysis variables
    if (data.fileAnalysis) {
      result = result.replace(/{{analytics}}/g, data.fileAnalysis.analytics || 'No analytics available');
      result = result.replace(/{{statistics}}/g, data.fileAnalysis.statistics || 'No statistics available');
      result = result.replace(/{{businessCase}}/g, data.fileAnalysis.businessCase || 'No business case available');
    }
    
    return result;
  }

  private generateProjectSummary(): string {
    const data = this.state.projectData;
    
    return `🎉 Product Setup Complete!

🏷️ Product Name: ${data.projectName}
📱 Product Type: ${data.projectType}
👥 Primary Users: ${data.targetUser}
💡 Solution Overview: ${data.solution}
⚠️ Pain Points Addressed: ${data.painPoints}

📁 Files: ${data.files?.length ? data.files.join(', ') : 'None uploaded'}

✅ Ready to create your product!`;
  }

  private checkBypassTrigger(input: string): boolean {
    const normalizedInput = input.toLowerCase();
    return normalizedInput === 'bypass' || normalizedInput.includes('demo');
  }

  private handleBypass(): string {
    this.state.projectData = { ...MOCK_PROJECT_DATA };
    this.state.currentStep = 'REVIEW';
    
    return `🚀 Demo data loaded!

🏷️ Product Name: ${MOCK_PROJECT_DATA.projectName}
👥 Primary Users: ${MOCK_PROJECT_DATA.targetUser}
💡 Solution Overview: ${MOCK_PROJECT_DATA.solution}
⚠️ Pain Points Addressed: ${MOCK_PROJECT_DATA.painPoints}

Demo data loaded for quick testing.

Type "Create Product" to proceed or "Edit Details" to modify.`;
  }

  private getCurrentStepPrompt(): string {
    return CHATBOT_STEPS[this.state.currentStep as DefinedChatbotStep]?.prompt || 'Let me help you set up your product.';
  }

  private addToHistory(role: 'user' | 'assistant', content: string): void {
    this.state.conversationHistory.push({
      role,
      content,
      timestamp: new Date()
    });
  }

  // Public getters
  public getCurrentStep(): string {
    return this.state.currentStep;
  }

  public getProjectData(): Partial<ProjectData> {
    return { ...this.state.projectData };
  }

  public getConversationHistory(): ConversationState['conversationHistory'] {
    return [...this.state.conversationHistory];
  }

  public isComplete(): boolean {
    return this.state.currentStep === 'COMPLETE' || this.hasAllRequiredData();
  }

  private hasAllRequiredData(): boolean {
    const required = ['projectType', 'projectName', 'targetUser', 'painPoints', 'solution'];
    return required.every(field => this.state.projectData[field as keyof ProjectData]);
  }

  // Handle quick edit commands from review step
  public handleQuickEdit(command: string, value: string): string {
    switch (command.toLowerCase()) {
      case 'name':
        this.state.projectData.projectName = value;
        return `✅ Product name updated to: ${value}`;
      case 'user':
        this.state.projectData.targetUser = value;
        return `✅ Target user updated to: ${value}`;
      case 'problems':
        this.state.projectData.painPoints = value;
        return `✅ Pain points updated to: ${value}`;
      case 'solution':
        this.state.projectData.solution = value;
        return `✅ Solution updated to: ${value}`;
      case 'restart':
        this.reset();
        return this.startConversation();
      default:
        return 'Invalid edit command.';
    }
  }

  // Reset conversation
  public reset(): void {
    this.state = {
      currentStep: 'TYPE',
      projectData: {},
      conversationHistory: [],
      stepHistory: []
    };
  }
}